<template>
  <section>
    <!-- レビューセクション -->
    <template v-if="voices.length !== 0">
      <h2 class="bg-beige1 pt-12 text-center text-xl font-normal md:pt-16 md:text-2xl">Reviews & Reflections</h2>
      <div class="bg-beige1 pb-7 pt-2 md:pb-14">
        <div class="mx-auto w-12 border-b border-gold1 md:w-14" />
      </div>
      <div class="bg-beige1 pb-12 md:pb-20">
        <div class="mx-4 xl:mx-auto xl:max-w-[1280px] xl:px-14">
          <!-- レビュー件数 -->
          <p class="mb-4 text-sm text-gray2">{{ voices.length }} Review{{ voices.length > 1 ? 's' : '' }}</p>
          <!-- 横スクロールカルーセル -->
          <div class="relative">
            <!-- 左矢印ボタン -->
            <button
              v-show="isLeftBtnVisible"
              class="absolute -left-12 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black text-white shadow-md xl:flex"
              @click="scrollLeft"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M15 19l-7-7 7-7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
              </svg>
            </button>
            <!-- スクロールコンテナ -->
            <div ref="scrollContainer" class="scrollbar-thin flex gap-4 overflow-x-auto scroll-smooth pb-6">
              <template v-for="(voice, index) in voices" :key="index + voice.content">
                <OrganismsCustomerVoice :voice="voice" />
              </template>
            </div>
            <!-- 右矢印ボタン -->
            <button
              v-show="isRightBtnVisible"
              class="absolute -right-12 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black text-white shadow-md xl:flex"
              @click="scrollRight"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M9 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script lang="ts" setup>
import type { CustomerVoiceViewModel } from '@/models/customerVoice';

interface Props {
  voices: CustomerVoiceViewModel[];
}

defineProps<Props>();

const scrollContainer = ref<HTMLElement>();
const isLeftBtnVisible = ref(false);
const isRightBtnVisible = ref(false);

const updateButtonVisibility = () => {
  if (!scrollContainer.value) return;
  const { scrollLeft, scrollWidth, clientWidth } = scrollContainer.value;
  const tolerance = 1;
  isLeftBtnVisible.value = scrollLeft > tolerance;
  isRightBtnVisible.value = scrollLeft + clientWidth < scrollWidth - tolerance;
};

const scrollLeft = () => {
  if (!scrollContainer.value) return;
  const scrollTo = Math.max(0, scrollContainer.value.scrollLeft - scrollContainer.value.clientWidth);
  scrollContainer.value.scrollTo({ left: scrollTo, behavior: 'smooth' });
};

const scrollRight = () => {
  if (!scrollContainer.value) return;
  const { scrollWidth, clientWidth, scrollLeft: currentScrollLeft } = scrollContainer.value;
  const scrollTo = Math.min(scrollWidth - clientWidth, currentScrollLeft + clientWidth);
  scrollContainer.value.scrollTo({ left: scrollTo, behavior: 'smooth' });
};

onMounted(() => {
  updateButtonVisibility();
  scrollContainer.value?.addEventListener('scroll', updateButtonVisibility);
  window.addEventListener('resize', updateButtonVisibility);
});

onBeforeUnmount(() => {
  scrollContainer.value?.removeEventListener('scroll', updateButtonVisibility);
  window.removeEventListener('resize', updateButtonVisibility);
});
</script>

<style lang="scss" scoped>
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #836324 transparent;
}
.scrollbar-thin::-webkit-scrollbar {
  height: 2px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: transparent;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #836324;
  border-radius: 2px;
}
</style>
